import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { buildMatchAnalysis } from '../../utils/matchAnalysis.js';
import {
  summarizeGoals,
  summarizeKicks,
  summarizePasses,
  summarizeDirectionalPasses,
  summarizeShots,
  summarizeSaves,
  summarizeSimpleList,
  summarizeDuels,
  summarizeRebounds,
  summarizeClearances,
  summarizeClearanceRecoveries,
  summarizeGoalkeeperTicks,
  buildAdvancedPlayerStats,
  extractCompletedPasses,
  extractDangerousTurnovers,
  extractPlayerXg,
  extractTurnoverTouches,
  resolvePlayerName,
  toFiniteNumber
} from '../../utils/gameStatsHelpers';
import { downloadCsv } from '../../utils/csvExport';

const FAQ_ITEMS = [
  {
    key: 'possessionTouches',
    question: 'What are Pos Touches?',
    answer: 'The total number of times a player controlled the ball while their team maintained possession. It is the foundation for most other ratios in the depth stats.'
  },
  {
    key: 'possessionEfficiency',
    question: 'What does Scoring % mean?',
    answer: 'The share of a player’s possession touches that fed into a scoring sequence. Higher values mean their touches often led to goals.'
  },
  {
    key: 'turnoverTouches',
    question: 'What is Turnov Touch?',
    answer: 'Touches that immediately gave the ball away. Less is better—think intercepted passes, miscontrols, or dispossessions.'
  },
  {
    key: 'forcedTurnovers',
    question: 'What are Forced TO?',
    answer: 'Takeaways caused by the player: tackles, interceptions, or pressure that directly wins the ball back for their team.'
  },
  {
    key: 'dangerousTurnovers',
    question: 'Why track Dang. TO?',
    answer: 'Dangerous turnovers happen in risky areas or lead to quick opponent chances. They highlight costly mistakes separately from routine giveaways.'
  },
  {
    key: 'progressivePasses',
    question: 'What is a Prog Pass?',
    answer: 'A forward pass that meaningfully advances the ball toward goal or breaks lines. It measures how much a player drives progression.'
  },
  {
    key: 'progressiveDistance',
    question: 'What is Prog Dist?',
    answer: 'The total distance gained from a player’s progressive passes and carries. It shows how many virtual meters they moved the ball upfield.'
  },
  {
    key: 'finalThirdEntries',
    question: 'What does Final 3rd track?',
    answer: 'Counts the times a player moved the ball into the attacking third with control. Entries mark who is breaking lines into dangerous zones.'
  },
  {
    key: 'keyPasses',
    question: 'What is a Key Pass?',
    answer: 'A pass that sets up a teammate shot shortly after. If their pass leads directly to a chance, it lands here.'
  },
  {
    key: 'recoveries',
    question: 'What are Recoveries?',
    answer: 'Moments where the player regained possession for their team after a loose ball or defensive action.'
  },
  {
    key: 'finalThirdRecoveries',
    question: 'What are F3 Recoveries?',
    answer: 'Recoveries made in the attacking third. They highlight high pressing or aggressive positioning near the opponent goal.'
  },
  {
    key: 'pressureRelief',
    question: 'What does Press Relief show?',
    answer: 'Press Relief is the percentage of possession touches without heavy pressure. Higher numbers mean the player stayed composed and avoided traps.'
  },
  {
    key: 'duels',
    question: 'What counts as Duels?',
    answer: 'Total 1v1 contests the player engaged in across the match, both on the ground and in the air.'
  },
  {
    key: 'duelsWon',
    question: 'What is Duel W?',
    answer: 'The number of duels the player won. More wins mean they came out on top in their direct battles.'
  },
  {
    key: 'duelsLost',
    question: 'What is Duel L?',
    answer: 'The number of duels the player lost. It helps balance the duel story alongside wins.'
  },
  {
    key: 'duelsInterception',
    question: 'What are Interc Duels?',
    answer: 'Duels won specifically by jumping a passing lane or intercepting the ball rather than physical challenges.'
  },
  {
    key: 'rebounds',
    question: 'What are Rebounds?',
    answer: 'Contests for loose balls that spray out after saves, blocks, or deflections.'
  },
  {
    key: 'reboundRecoveries',
    question: 'What is Reb Rec?',
    answer: 'Times the player’s team secured the ball on those rebound contests because of them.'
  },
  {
    key: 'clearances',
    question: 'What are Clear?',
    answer: 'Clearances where the player safely removes the ball from danger, usually from their defensive third.'
  },
  {
    key: 'clearanceRecoveries',
    question: 'What is Cl Rec?',
    answer: 'Follow-up possessions won by the same team after the player’s clearance, showing how often their clearances kept control.'
  },
  {
    key: 'xgFaced',
    question: 'What does XG Faced mean?',
    answer: 'The expected goal value of shots directed at the player’s zone or responsibility. Larger numbers mean they were tested more.'
  },
  {
    key: 'xgPrevented',
    question: 'What is XG Prev?',
    answer: 'Expected goals prevented compares shots faced to results. Positive values mean the player (usually keeper/defender) saved more than the model expected.'
  },
  {
    key: 'sweeperActions',
    question: 'What are Sweeper actions?',
    answer: 'Emergency defensive sweeps—think keeper rushing off the line or last defender cleaning up behind the line.'
  },
  {
    key: 'secondaryChain',
    question: 'What is 2nd Ast?',
    answer: 'Secondary assist credit: the pass (or action) that set up the assist. It notes supporting playmakers.'
  },
  {
    key: 'tertiaryChain',
    question: 'What is 3rd Ast?',
    answer: 'Tertiary chain involvement: the action before the secondary assist, capturing players who start the scoring move.'
  },
  {
    key: 'counterRuns',
    question: 'What are Counters?',
    answer: 'Counter runs the player initiated or carried significantly after turnovers, highlighting transition threats.'
  }
];

const normalizeKey = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.toLowerCase();
};

const findMapEntry = (map, key) => {
  if (!map || typeof map.entries !== 'function') return undefined;
  if (map.has && map.has(key)) return map.get(key);
  const normalized = normalizeKey(key);
  if (!normalized) return undefined;
  for (const [entryKey, value] of map.entries()) {
    if (typeof entryKey === 'string' && normalizeKey(entryKey) === normalized) {
      return value;
    }
  }
  return undefined;
};

const getCountLoose = (map, key) => {
  const entry = findMapEntry(map, key);
  if (Number.isFinite(entry)) return Number(entry);
  if (entry && typeof entry === 'object') {
    if (Number.isFinite(entry.total)) return Number(entry.total);
    if (Number.isFinite(entry.count)) return Number(entry.count);
  }
  return 0;
};

const firstFinite = (...values) => {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return 0;
};

const formatCsvInteger = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return String(Math.round(num));
};

const formatCsvDecimal = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return digits === 0 ? '0' : (0).toFixed(digits);
  return num.toFixed(digits);
};

const formatCsvPercent = (value, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return `${num.toFixed(digits)}%`;
};

const formatCsvFractionPercent = (value, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return `${(num * 100).toFixed(digits)}%`;
};

const sanitizeFilenamePart = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return '';
  return trimmed
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const buildExportFilename = (match, index) => {
  const fallback = typeof index === 'number' ? `depth-stats-match-${index + 1}` : 'depth-stats';
  if (!match) return `${fallback}.csv`;

  const nameCandidate =
    match.matchId ||
    match.id ||
    match.roomName ||
    match.name ||
    match.title ||
    (match.metadata && match.metadata.name) ||
    '';
  const dateCandidate = match.startTime || match.date || match.matchDate || match.recordedAt;

  const sanitizedName = sanitizeFilenamePart(nameCandidate);
  let sanitizedDate = '';
  if (dateCandidate) {
    const date = new Date(dateCandidate);
    if (!Number.isNaN(date.getTime())) {
      const year = String(date.getUTCFullYear());
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      sanitizedDate = `${year}${month}${day}-${hours}${minutes}`;
    }
  }

  const parts = [sanitizedName, sanitizedDate, 'depth-stats'].filter(Boolean);
  if (!parts.length) return `${fallback}.csv`;
  return `${parts.join('-')}.csv`;
};

function buildNormalStatMap(match) {
  if (!match) return new Map();

  const players = Array.isArray(match.player) ? match.player : [];
  if (!players.length) return new Map();

  const goals = Array.isArray(match.goals) ? match.goals : [];
  const kicks = Array.isArray(match.kicks) ? match.kicks : [];
  const passes = Array.isArray(match.passes) ? match.passes : [];
  const shots = Array.isArray(match.shots) ? match.shots : [];
  const saves = Array.isArray(match.saves) ? match.saves : [];
  const bumps = Array.isArray(match.bumps) ? match.bumps : [];
  const touches = Array.isArray(match.touches) ? match.touches : [];
  const duels = Array.isArray(match.duels) ? match.duels : [];
  const reboundsList = Array.isArray(match.rebounds) ? match.rebounds : [];
  const clearancesList = Array.isArray(match.clearances) ? match.clearances : [];
  const clearanceRecoveriesList = Array.isArray(match.clearanceRecoveries) ? match.clearanceRecoveries : [];
  const gkList = Array.isArray(match.gkOccupancy) ? match.gkOccupancy : [];

  const redTeam = Array.isArray(match.redTeam) ? match.redTeam : [];
  const blueTeam = Array.isArray(match.blueTeam) ? match.blueTeam : [];
  const redSet = new Set(redTeam || []);
  const blueSet = new Set(blueTeam || []);

  const advancedStats = buildAdvancedPlayerStats(match) || {};
  const advancedMap = advancedStats.advanced instanceof Map ? advancedStats.advanced : new Map();

  const { goals: goalMap, assists: assistMap } = summarizeGoals(goals);
  const kicksMap = summarizeKicks(kicks);
  const passMap = summarizePasses(passes);
  const directionalPassMap = summarizeDirectionalPasses(passes);
  const { byShooter: shotMap, savedBy: shotSavedBy } = summarizeShots(shots, {
    stadium: match.stadium,
    redTeam,
    blueTeam
  });
  const savesMap = summarizeSaves(saves, shotSavedBy);
  const bumpsMap = summarizeSimpleList(bumps);
  const touchesMap = summarizeSimpleList(touches);
  const duelsMap = summarizeDuels(duels);
  const reboundsMap = summarizeRebounds(reboundsList);
  const clearancesMap = summarizeClearances(clearancesList);
  const clearanceRecoveriesMap = summarizeClearanceRecoveries(clearanceRecoveriesList);
  const gkMap = summarizeGoalkeeperTicks(gkList);

  const analysisPlayers = Array.isArray(match.players) ? match.players : [];
  const analysisMap = new Map();
  analysisPlayers.forEach((entry) => {
    if (!entry) return;
    const mappedNick = entry.nick || entry.player || entry.name || resolvePlayerName(entry);
    if (!mappedNick) return;
    const metrics = entry.metrics && typeof entry.metrics === 'object' ? entry.metrics : entry;
    analysisMap.set(mappedNick, metrics);
  });

  const result = new Map();

  players.forEach((player) => {
    if (!player) return;
    const nick = player.nick || player.player || player.name || resolvePlayerName(player);
    if (!nick) return;

    const analysisMetrics = findMapEntry(analysisMap, nick) || {};
    const advancedMetrics = findMapEntry(advancedMap, nick) || {};

    const metricGoals = toFiniteNumber(player.metrics?.goals) || 0;
    const metricAssists = toFiniteNumber(player.metrics?.assists) || 0;
    const metricKicks = toFiniteNumber(player.metrics?.kicks) || 0;
    const goalsCount = Math.max(getCountLoose(goalMap, nick), metricGoals);
    const assistsCount = Math.max(getCountLoose(assistMap, nick), metricAssists);
    const kicksCount = Math.max(getCountLoose(kicksMap, nick), metricKicks);

    const passesCompletedMetric = toFiniteNumber(analysisMetrics.passesCompleted) || 0;
    const passesCompleted = passesCompletedMetric > 0
      ? passesCompletedMetric
      : Math.max(
          getCountLoose(passMap, nick),
          extractCompletedPasses(player, 0),
          toFiniteNumber(player.metrics?.passesCompleted) || 0,
          toFiniteNumber(advancedMetrics.passesCompleted) || 0,
          toFiniteNumber(player.passesCompleted) || 0
        );
    const directionalCompleted = findMapEntry(directionalPassMap, nick) || { forward: 0, lateral: 0, backward: 0 };

    const { total: shotTotal = 0, onTarget = 0, xg = 0 } = findMapEntry(shotMap, nick) || {};
    const shotInfo = {
      total: Number(shotTotal) || 0,
      onTarget: Number(onTarget) || 0,
      xg: Number(xg) || 0
    };

    const savesCount = Math.max(
      getCountLoose(savesMap, nick),
      toFiniteNumber(player.metrics?.saves) || 0,
      toFiniteNumber(player.saves) || 0,
      toFiniteNumber(player.goalkeeping?.saves) || 0,
      toFiniteNumber(analysisMetrics.saves) || 0
    );

    const bumpsCount = Math.max(
      getCountLoose(bumpsMap, nick),
      toFiniteNumber(player.bumps) || 0,
      toFiniteNumber(player.contacts) || 0,
      toFiniteNumber(analysisMetrics.bumps) || 0
    );

    const touchesCount = Math.max(
      getCountLoose(touchesMap, nick),
      toFiniteNumber(player.touches) || 0,
      toFiniteNumber(player.totalTouches) || 0,
      toFiniteNumber(player.touchCount) || 0,
      toFiniteNumber(analysisMetrics.touches) || 0
    );

    const duelsEntry = findMapEntry(duelsMap, nick) || { total: 0 };
    const reboundsEntry = findMapEntry(reboundsMap, nick) || { total: 0, clearanceRecoveries: 0 };
    const clearancesEntry = findMapEntry(clearancesMap, nick) || { total: 0 };
    const clearanceRecoveryEntry = findMapEntry(clearanceRecoveriesMap, nick) || { total: 0 };
    const gkEntry = findMapEntry(gkMap, nick) || { ticks: 0 };

    const rawOffTarget = Number.isFinite(shotInfo.total)
      ? (shotInfo.total || 0) - (shotInfo.onTarget || 0)
      : 0;
    const shotsOffTarget = Math.max(
      rawOffTarget || 0,
      toFiniteNumber(player.shotsOffTarget) || 0,
      toFiniteNumber(player.shotsWide) || 0,
      toFiniteNumber(player.missedShots) || 0,
      toFiniteNumber(analysisMetrics.shotsOffTarget) || 0
    );

    const shotsOnTarget = Math.max(
      shotInfo.onTarget || 0,
      toFiniteNumber(player.targetedShots) || 0,
      toFiniteNumber(player.shotsOnTarget) || 0,
      toFiniteNumber(analysisMetrics.shotsOnTarget) || 0
    );

    const xgFromShots = shotInfo.xg || 0;
    const playerXg = extractPlayerXg(player, 0);
    const metricsXg = toFiniteNumber(analysisMetrics.xg) || 0;
    const xgTotal = metricsXg > 0 ? metricsXg : (playerXg > 0 ? playerXg : xgFromShots);

    const turnoverTouches = Math.max(
      extractTurnoverTouches(analysisMetrics, 0),
      extractTurnoverTouches(player, 0),
      extractTurnoverTouches(advancedMetrics, 0)
    );
    const dangerousTouches = Math.max(
      extractDangerousTurnovers(analysisMetrics, 0),
      extractDangerousTurnovers(player, 0),
      extractDangerousTurnovers(advancedMetrics, 0)
    );
    const passAttempts = Math.max(0, passesCompleted + turnoverTouches + dangerousTouches);
    const passAccuracyPct = passAttempts > 0 ? (passesCompleted / passAttempts) * 100 : null;

    result.set(nick, {
      nick,
      team: redSet.has(nick) ? 'red' : (blueSet.has(nick) ? 'blue' : 'spec'),
      goals: Math.round(goalsCount) || 0,
      assists: Math.round(assistsCount) || 0,
      kicks: Math.round(kicksCount) || 0,
      passesCompleted: Math.round(passesCompleted) || 0,
      passAttempts,
      passAccuracyPct,
      passesForwardCompleted: Number(directionalCompleted.forward || 0),
      passesLateralCompleted: Number(directionalCompleted.lateral || 0),
      passesBackwardCompleted: Number(directionalCompleted.backward || 0),
      keyPasses: Number(analysisMetrics.keyPasses || 0),
      saves: Math.round(savesCount) || 0,
      xg: Number.isFinite(xgTotal) ? Number(xgTotal) : 0,
      shotsOnTarget: Math.round(shotsOnTarget) || 0,
      shotsOffTarget: Math.round(shotsOffTarget) || 0,
      bumps: Math.round(bumpsCount) || 0,
      touches: Math.round(touchesCount) || 0,
      duelsTotal: Number(duelsEntry.total || 0),
      duelsWon: Number(duelsEntry.wins || 0),
      duelsLost: Number(duelsEntry.losses || 0),
      duelsInterception: Number(duelsEntry.interception || 0),
      reboundsTotal: Number(reboundsEntry.total || 0),
      reboundClearanceRecoveries: Number(reboundsEntry.clearanceRecoveries || 0),
      clearancesTotal: Number(clearancesEntry.total || 0),
      clearanceRecoveries: Number(clearanceRecoveryEntry.total || 0),
      gkTicks: Number(gkEntry.ticks || 0)
    });
  });

  return result;
}

function buildCsvDataset(match, depthRows, matchIndex) {
  if (!match || !Array.isArray(depthRows) || !depthRows.length) return null;

  const normalStatMap = buildNormalStatMap(match);

  const csvRows = depthRows.map((depthRow) => {
    const base = normalStatMap.get(depthRow.nick) || {};
    const team = depthRow.team || base.team || '';

    return {
      nick: depthRow.nick || base.nick || '',
      team,
      position: depthRow.position || base.position || '',
      goals: firstFinite(base.goals),
      assists: firstFinite(base.assists),
      shotsOnTarget: firstFinite(base.shotsOnTarget),
      shotsOffTarget: firstFinite(base.shotsOffTarget),
      xg: firstFinite(base.xg),
      kicks: firstFinite(base.kicks),
      saves: firstFinite(base.saves),
      passesCompleted: firstFinite(base.passesCompleted),
      passAttempts: firstFinite(base.passAttempts),
      passAccuracyPct: Number.isFinite(base.passAccuracyPct) ? base.passAccuracyPct : null,
      passesForwardCompleted: firstFinite(base.passesForwardCompleted, depthRow.passesForward),
      passesLateralCompleted: firstFinite(base.passesLateralCompleted),
      passesBackwardCompleted: firstFinite(base.passesBackwardCompleted, depthRow.passesBackward),
      keyPasses: firstFinite(depthRow.keyPasses, base.keyPasses),
      progressivePasses: firstFinite(depthRow.progressivePasses),
      progressiveDistance: firstFinite(depthRow.progressiveDistance),
      touches: firstFinite(base.touches),
      possessionTouches: firstFinite(depthRow.possessionTouches),
      possessionEfficiency: Number(depthRow.possessionEfficiency),
      pressureRelief: Number(depthRow.pressureRelief),
      turnoverTouches: firstFinite(depthRow.turnoverTouches),
      dangerousTurnovers: firstFinite(depthRow.dangerousTurnovers),
      forcedTurnovers: firstFinite(depthRow.forcedTurnovers),
      turnoverCount: firstFinite(depthRow.turnoverCount),
      recoveries: firstFinite(depthRow.recoveries),
      finalThirdRecoveries: firstFinite(depthRow.finalThirdRecoveries),
      finalThirdEntries: firstFinite(depthRow.finalThirdEntries),
      duels: firstFinite(depthRow.duels, base.duelsTotal),
      duelsWon: firstFinite(depthRow.duelsWon, base.duelsWon),
      duelsLost: firstFinite(depthRow.duelsLost, base.duelsLost),
      duelsInterception: firstFinite(depthRow.duelsInterception, base.duelsInterception),
      rebounds: firstFinite(depthRow.rebounds, base.reboundsTotal),
      reboundRecoveries: firstFinite(depthRow.reboundRecoveries, base.reboundClearanceRecoveries),
      clearances: firstFinite(depthRow.clearances, base.clearancesTotal),
      clearanceRecoveries: firstFinite(depthRow.clearanceRecoveries, base.clearanceRecoveries),
      sweeperActions: firstFinite(depthRow.sweeperActions),
      counterRuns: firstFinite(depthRow.counterRuns),
      xgFaced: firstFinite(depthRow.xgFaced),
      xgPrevented: firstFinite(depthRow.xgPrevented),
      secondaryChain: firstFinite(depthRow.secondaryChain),
      tertiaryChain: firstFinite(depthRow.tertiaryChain),
      bumps: firstFinite(base.bumps),
      gkTicks: firstFinite(base.gkTicks)
    };
  });

  const columns = [
    { label: 'Player', value: (row) => row.nick || '' },
    { label: 'Team', value: (row) => (row.team ? row.team.toUpperCase() : '') },
    { label: 'Position', value: (row) => row.position || '' },
    { label: 'Goals', value: (row) => formatCsvInteger(row.goals) },
    { label: 'Assists', value: (row) => formatCsvInteger(row.assists) },
    { label: 'Shots On Target', value: (row) => formatCsvInteger(row.shotsOnTarget) },
    { label: 'Shots Off Target', value: (row) => formatCsvInteger(row.shotsOffTarget) },
    { label: 'xG', value: (row) => formatCsvDecimal(row.xg, 2) },
    { label: 'Kicks', value: (row) => formatCsvInteger(row.kicks) },
    { label: 'Saves', value: (row) => formatCsvInteger(row.saves) },
    { label: 'Passes Completed', value: (row) => formatCsvInteger(row.passesCompleted) },
    { label: 'Pass Attempts', value: (row) => formatCsvInteger(row.passAttempts) },
    { label: 'Pass Accuracy %', value: (row) => formatCsvPercent(row.passAccuracyPct, 1) },
    { label: 'Forward Passes Completed', value: (row) => formatCsvInteger(row.passesForwardCompleted) },
    { label: 'Lateral Passes Completed', value: (row) => formatCsvInteger(row.passesLateralCompleted) },
    { label: 'Backward Passes Completed', value: (row) => formatCsvInteger(row.passesBackwardCompleted) },
    { label: 'Key Passes', value: (row) => formatCsvInteger(row.keyPasses) },
    { label: 'Progressive Passes', value: (row) => formatCsvInteger(row.progressivePasses) },
    { label: 'Progressive Distance', value: (row) => formatCsvDecimal(row.progressiveDistance, 1) },
    { label: 'Touches', value: (row) => formatCsvInteger(row.touches) },
    { label: 'Possession Touches', value: (row) => formatCsvInteger(row.possessionTouches) },
    { label: 'Scoring %', value: (row) => formatCsvFractionPercent(row.possessionEfficiency, 1) },
    { label: 'Pressure Relief %', value: (row) => formatCsvFractionPercent(row.pressureRelief, 1) },
    { label: 'Turnover Touches', value: (row) => formatCsvInteger(row.turnoverTouches) },
    { label: 'Dangerous Turnovers', value: (row) => formatCsvInteger(row.dangerousTurnovers) },
    { label: 'Forced Turnovers', value: (row) => formatCsvInteger(row.forcedTurnovers) },
    { label: 'Total Turnovers', value: (row) => formatCsvInteger(row.turnoverCount) },
    { label: 'Recoveries', value: (row) => formatCsvInteger(row.recoveries) },
    { label: 'Final Third Recoveries', value: (row) => formatCsvInteger(row.finalThirdRecoveries) },
    { label: 'Final Third Entries', value: (row) => formatCsvInteger(row.finalThirdEntries) },
    { label: 'Duels', value: (row) => formatCsvInteger(row.duels) },
    { label: 'Duels Won', value: (row) => formatCsvInteger(row.duelsWon) },
    { label: 'Duels Lost', value: (row) => formatCsvInteger(row.duelsLost) },
    { label: 'Interception Duels', value: (row) => formatCsvInteger(row.duelsInterception) },
    { label: 'Rebounds', value: (row) => formatCsvInteger(row.rebounds) },
    { label: 'Rebound Recoveries', value: (row) => formatCsvInteger(row.reboundRecoveries) },
    { label: 'Clearances', value: (row) => formatCsvInteger(row.clearances) },
    { label: 'Clearance Recoveries', value: (row) => formatCsvInteger(row.clearanceRecoveries) },
    { label: 'Sweeper Actions', value: (row) => formatCsvInteger(row.sweeperActions) },
    { label: 'Counter Runs', value: (row) => formatCsvInteger(row.counterRuns) },
    { label: 'xG Faced', value: (row) => formatCsvDecimal(row.xgFaced, 2) },
    { label: 'xG Prevented', value: (row) => formatCsvDecimal(row.xgPrevented, 2) },
    { label: 'Secondary Chain', value: (row) => formatCsvInteger(row.secondaryChain) },
    { label: 'Tertiary Chain', value: (row) => formatCsvInteger(row.tertiaryChain) },
    { label: 'Bumps', value: (row) => formatCsvInteger(row.bumps) },
    { label: 'GK Ticks', value: (row) => formatCsvInteger(row.gkTicks) }
  ];

  const filename = buildExportFilename(match, matchIndex);

  return { filename, columns, rows: csvRows };
}

function buildPlayerRows(match) {
  if (!match) return [];
  const analysis = buildMatchAnalysis(match);
  if (!analysis || !Array.isArray(analysis.playerSummaries)) return [];

  return analysis.playerSummaries
    .filter((summary) => summary && (summary.team === 'red' || summary.team === 'blue'))
    .map((summary) => {
      const metrics = summary.metrics || {};
      const position = summary.position || summary.role || '';
      const possessionTouches = Number(metrics.possessionTouches) || 0;
      const scoringTouches = Number(metrics.scoringPossessionTouches) || 0;
      const turnoverTouches = Number(metrics.turnoverTouches) || 0;
      const pressuredTouches = Number(metrics.pressuredPossessionTouches) || 0;
      const forcedTurnovers = Number(metrics.forcedTurnovers) || 0;
      const progressivePasses = Number(metrics.progressivePasses) || 0;
      const progressiveDistance = Number(metrics.progressiveDistance) || 0;
      const finalThirdEntries = Number(metrics.finalThirdEntries) || 0;
      const keyPasses = Number(metrics.keyPasses) || 0;
      const recoveries = Number(metrics.recoveries) || 0;
      const finalThirdRecoveries = Number(metrics.finalThirdRecoveries) || 0;
      const xgPrevented = Number(metrics.xgPrevented) || 0;
      const xgFaced = Number(metrics.xgFaced) || 0;
      const sweeperActions = Number(metrics.sweeperActions) || 0;
      const secondaryChain = Number(metrics.secondaryAssistsChain) || 0;
      const tertiaryChain = Number(metrics.tertiaryAssistsChain) || 0;
      const counterRuns = Number(metrics.counterRuns) || 0;
      const turnoverCount = Number(metrics.turnoversCommitted) || 0;
      const dangerousTurnovers = Number(metrics.dangerousTurnovers) || 0;
      const possessionEfficiency = possessionTouches > 0 ? (scoringTouches / possessionTouches) : 0;
      const pressureRelief = possessionTouches > 0 ? 1 - (pressuredTouches / possessionTouches) : 0;

      return {
        nick: summary.nick,
        team: summary.team,
        position,
        duels: Number(metrics.duelsTotal) || 0,
        duelsWon: Number(metrics.duelsWon) || 0,
        duelsLost: Number(metrics.duelsLost) || 0,
        duelsInterception: Number(metrics.duelsInterception) || 0,
        rebounds: Number(metrics.rebounds) || 0,
        reboundRecoveries: Number(metrics.reboundRecoveries) || 0,
        clearances: Number(metrics.clearances) || 0,
        clearanceRecoveries: Number(metrics.clearanceRecoveries) || 0,
        passesForward: Number(metrics.passesForward) || 0,
        passesBackward: Number(metrics.passesBackward) || 0,
        possessionTouches,
        possessionEfficiency,
        turnoverTouches,
        dangerousTurnovers,
        turnoverCount,
        forcedTurnovers,
        progressivePasses,
        progressiveDistance,
        finalThirdEntries,
        keyPasses,
        recoveries,
        finalThirdRecoveries,
        pressureRelief,
        xgFaced,
        xgPrevented,
        sweeperActions,
        secondaryChain,
        tertiaryChain,
        counterRuns
      };
    });
}

function formatNumber(value, precision = 2) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toFixed(precision);
}

export default function PlayerDepthStats() {
  const matches = useSelector((state) => state.gameStats.matches) || [];
  const mtc = useSelector((state) => state.gameStats.selectedMatch) || 0;
  const currentMatch = matches[mtc] || null;

  const rows = useMemo(() => buildPlayerRows(currentMatch), [currentMatch]);
  const csvDataset = useMemo(() => buildCsvDataset(currentMatch, rows, mtc), [currentMatch, rows, mtc]);
  const [faqOpen, setFaqOpen] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState(FAQ_ITEMS[0]?.key || null);

  const exportDisabled = !csvDataset || !csvDataset.rows.length;

  const handleExport = () => {
    if (exportDisabled) return;
    downloadCsv({
      filename: csvDataset.filename,
      columns: csvDataset.columns,
      rows: csvDataset.rows
    });
  };

  if (!rows.length) {
    return (
      <div className="gs-pro-empty">No depth stats available for this match.</div>
    );
  }

  const teamWeight = (team) => {
    if (team === 'red') return 0;
    if (team === 'blue') return 1;
    return 2;
  };

  const sorted = [...rows].sort((a, b) => {
    const teamDiff = teamWeight(a.team) - teamWeight(b.team);
    if (teamDiff !== 0) return teamDiff;
    return (b.progressivePasses || 0) - (a.progressivePasses || 0);
  });

  return (
    <div className="gs-pro-depth">
      <div className="gs-pro-faq">
        <div className="gs-pro-faq-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="gs-pro-faq-toggle"
            onClick={() => setFaqOpen((prev) => !prev)}
          >
            {faqOpen ? 'Hide Depth Stats FAQ' : 'Depth Stats FAQ'}
          </button>
          <button
            type="button"
            className="gs-pro-faq-toggle"
            onClick={handleExport}
            disabled={exportDisabled}
            aria-disabled={exportDisabled}
            title={exportDisabled ? 'No data available to export' : 'Download depth stats as CSV'}
          >
            Export CSV
          </button>
        </div>
        {faqOpen && (
          <div className="gs-pro-faq-panel" role="region" aria-label="Depth stats explanations">
            <div className="gs-pro-faq-questions" role="tablist">
              {FAQ_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`gs-pro-faq-question${activeQuestion === item.key ? ' active' : ''}`}
                  onClick={() => setActiveQuestion((prev) => (prev === item.key ? null : item.key))}
                  role="tab"
                  aria-selected={activeQuestion === item.key}
                >
                  {item.question}
                </button>
              ))}
            </div>
            {activeQuestion && (
              <div className="gs-pro-faq-answer" role="tabpanel">
                {FAQ_ITEMS.filter((item) => item.key === activeQuestion).map((item) => (
                  <div key={item.key}>
                    <h4>{item.question}</h4>
                    <p>{item.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="gs-pro-table-scroll">
        <table className="gs-pro-table">
      <thead>
        <tr>
          <th scope="col">Player</th>
          <th scope="col">Team</th>
          <th scope="col">Pos</th>
          <th scope="col">Pos Touches</th>
          <th scope="col">Scoring %</th>
          <th scope="col">Turnov Touch</th>
          <th scope="col">Forced TO</th>
          <th scope="col">Dang. TO</th>
          <th scope="col">Prog Pass</th>
          <th scope="col">Prog Dist</th>
          <th scope="col">Final 3rd</th>
          <th scope="col">Key Pass</th>
          <th scope="col">Recoveries</th>
          <th scope="col">F3 Recoveries</th>
          <th scope="col">Press Relief</th>
          <th scope="col">Duels</th>
          <th scope="col">Duel W</th>
          <th scope="col">Duel L</th>
          <th scope="col">Interc Duels</th>
          <th scope="col">Rebounds</th>
          <th scope="col">Reb Rec</th>
          <th scope="col">Clear</th>
          <th scope="col">Clr Rec</th>
          <th scope="col">XG Faced</th>
          <th scope="col">XG Prev</th>
          <th scope="col">Sweeper</th>
          <th scope="col">2nd Ast</th>
          <th scope="col">3rd Ast</th>
          <th scope="col">Counters</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <tr key={row.nick}>
            <td>{row.nick}</td>
            <td className={row.team}>{row.team.toUpperCase()}</td>
            <td className="center">{row.position}</td>
            <td className="center">{formatNumber(row.possessionTouches, 0)}</td>
            <td className="center">{formatNumber(row.possessionEfficiency * 100, 1)}%</td>
            <td className="center">{formatNumber(row.turnoverTouches, 0)}</td>
            <td className="center">{formatNumber(row.forcedTurnovers, 0)}</td>
            <td className="center">{formatNumber(row.dangerousTurnovers, 0)}</td>
            <td className="center">{formatNumber(row.progressivePasses, 0)}</td>
            <td className="center">{formatNumber(row.progressiveDistance, 1)}</td>
            <td className="center">{formatNumber(row.finalThirdEntries, 0)}</td>
            <td className="center">{formatNumber(row.keyPasses, 0)}</td>
            <td className="center">{formatNumber(row.recoveries, 0)}</td>
            <td className="center">{formatNumber(row.finalThirdRecoveries, 0)}</td>
            <td className="center">{formatNumber(row.pressureRelief * 100, 1)}%</td>
            <td className="center">{formatNumber(row.duels, 0)}</td>
            <td className="center">{formatNumber(row.duelsWon, 0)}</td>
            <td className="center">{formatNumber(row.duelsLost, 0)}</td>
            <td className="center">{formatNumber(row.duelsInterception, 0)}</td>
            <td className="center">{formatNumber(row.rebounds, 0)}</td>
            <td className="center">{formatNumber(row.reboundRecoveries, 0)}</td>
            <td className="center">{formatNumber(row.clearances, 0)}</td>
            <td className="center">{formatNumber(row.clearanceRecoveries, 0)}</td>
            <td className="center">{formatNumber(row.xgFaced, 2)}</td>
            <td className="center">{formatNumber(row.xgPrevented, 2)}</td>
            <td className="center">{formatNumber(row.sweeperActions, 0)}</td>
            <td className="center">{formatNumber(row.secondaryChain, 0)}</td>
            <td className="center">{formatNumber(row.tertiaryChain, 0)}</td>
            <td className="center">{formatNumber(row.counterRuns, 0)}</td>
          </tr>
        ))}
      </tbody>
        </table>
      </div>
    </div>
  );
}
